import { BaseGenerationJobHandler, ensureUniqueTitle } from "@brains/plugins";
import { GENERATE_CHANNELS, IMAGE_CHANNELS } from "@brains/contracts";
import type { GeneratedContent } from "@brains/plugins";
import type { Logger } from "@brains/utils/logger";
import type { ProgressReporter } from "@brains/utils/progress";
import { slugify } from "@brains/utils/string-utils";
import { z } from "@brains/utils/zod";
import { generationResultSchema } from "@brains/contracts";
import { fetchStyleGuide, formatVoiceGuidance } from "@brains/contracts";
import type { EntityPluginContext } from "@brains/plugins";
import type { SocialPostFrontmatter } from "../schemas/social-post";
import { socialPostAdapter } from "../adapters/social-post-adapter";
import { getTemplateName } from "../templates";

/** Shape the social generation templates produce; ai.generate parses through it. */
export const generatedPostSchema: z.ZodObject<{
  title: z.ZodString;
  content: z.ZodString;
}> = z.object({
  title: z.string(),
  content: z.string(),
});

/**
 * Input schema for social post generation job
 */
interface CoverImageJobOptions {
  generate?: boolean | undefined;
  prompt?: string | undefined;
}

type CoverImageJobData = boolean | CoverImageJobOptions;

const coverImageJobSchema: z.ZodType<CoverImageJobData> = z.union([
  z.boolean(),
  z.object({
    generate: z.boolean().optional(),
    prompt: z.string().optional(),
  }),
]);

export interface GenerationJobData {
  prompt?: string | undefined;
  platform?: "linkedin" | undefined;
  sourceEntityType?: "post" | "deck" | undefined;
  sourceEntityId?: string | undefined;
  title?: string | undefined;
  content?: string | undefined;
  addToQueue?: boolean | undefined;
  generateImage?: boolean | undefined;
  coverImage?: CoverImageJobData | undefined;
}

export const generationJobSchema: z.ZodType<GenerationJobData> = z.object({
  prompt: z.string().optional(),
  platform: z.enum(["linkedin"]).optional(),
  sourceEntityType: z.enum(["post", "deck"]).optional(),
  sourceEntityId: z.string().optional(),
  title: z
    .string()
    .optional()
    .describe("Required when content is provided directly"),
  content: z.string().optional(),
  addToQueue: z.boolean().optional(),
  generateImage: z
    .boolean()
    .optional()
    .describe("Auto-generate cover image for post"),
  coverImage: coverImageJobSchema
    .optional()
    .describe("Generic cover image generation request"),
});

export const socialMediaGenerationResultSchema: ReturnType<
  typeof generationResultSchema.extend<{
    slug: z.ZodOptional<z.ZodString>;
  }>
> = generationResultSchema.extend({
  slug: z.string().optional(),
});

export type GenerationResult = z.output<
  typeof socialMediaGenerationResultSchema
>;

export class GenerationJobHandler extends BaseGenerationJobHandler<
  GenerationJobData,
  GenerationResult
> {
  constructor(logger: Logger, context: EntityPluginContext) {
    super(logger, context, {
      schema: generationJobSchema,
      jobTypeName: "social-post-generation",
      entityType: "social-post",
    });
  }

  protected async generate(
    data: GenerationJobData,
    progressReporter: ProgressReporter,
  ): Promise<GeneratedContent> {
    const platform = data.platform ?? "linkedin";
    const addToQueue = data.addToQueue ?? false;
    const { prompt, sourceEntityType, sourceEntityId } = data;
    let { content, title } = data;
    const voiceGuidance =
      content && title
        ? ""
        : formatVoiceGuidance(
            await fetchStyleGuide(this.context.entityService),
          );
    const styleContext = {
      representedIdentity: "anchor" as const,
      ...(voiceGuidance && { styleGuide: { voice: voiceGuidance } }),
    };

    // Case 1: Direct content with title (no AI needed)
    if (content && title) {
      await this.reportProgress(progressReporter, {
        progress: 50,
        message: "Using provided content",
      });
    }
    // Case 1b: Content without title — pass through AI to shape and generate title
    else if (content && !title) {
      await this.reportProgress(progressReporter, {
        progress: 10,
        message: "Shaping content with AI",
      });

      const generated = await this.context.ai.generate(
        {
          prompt: content,
          templateName: getTemplateName(platform),
          ...styleContext,
        },
        generatedPostSchema,
      );

      title = generated.title;
      content = generated.content;

      await this.reportProgress(progressReporter, {
        progress: 50,
        message: "Social post shaped from content",
      });
    }
    // Case 2: Generate from source entity
    else if (sourceEntityId && sourceEntityType) {
      await this.reportProgress(progressReporter, {
        progress: 10,
        message: `Fetching source ${sourceEntityType}`,
      });

      const sourceEntity = await this.context.entityService.getEntity({
        entityType: sourceEntityType,
        id: sourceEntityId,
      });

      if (!sourceEntity) {
        this.failEarly(
          `Source entity not found: ${sourceEntityType}/${sourceEntityId}`,
        );
      }

      await this.reportProgress(progressReporter, {
        progress: 30,
        message: "Generating social post from source content",
      });

      const slugSchema = z.looseObject({ slug: z.string() });
      const parsed = slugSchema.safeParse(sourceEntity.metadata);
      const slug = parsed.success ? parsed.data.slug : sourceEntityId;

      const generated = await this.context.ai.generate(
        {
          prompt: `Create an engaging ${platform} post to promote this ${sourceEntityType}:

Source: ${sourceEntityType}/${slug}

${sourceEntity.content}`,
          templateName: getTemplateName(platform),
          ...styleContext,
        },
        generatedPostSchema,
      );

      title = generated.title;
      content = generated.content;

      await this.reportProgress(progressReporter, {
        progress: 50,
        message: "Social post generated from source",
      });
    }
    // Case 3: Generate from prompt
    else if (prompt) {
      await this.reportProgress(progressReporter, {
        progress: 10,
        message: "Generating social post with AI",
      });

      const generated = await this.context.ai.generate(
        {
          prompt,
          templateName: getTemplateName(platform),
          ...styleContext,
        },
        generatedPostSchema,
      );

      title = generated.title;
      content = generated.content;

      await this.reportProgress(progressReporter, {
        progress: 50,
        message: "Social post generated",
      });
    } else {
      this.failEarly(
        "No content source provided (prompt, sourceEntityId, or content)",
      );
    }

    if (!content || !title) {
      this.failEarly("Content or title was not generated");
    }

    const status = addToQueue ? "queued" : "draft";

    // Create frontmatter
    const frontmatter: SocialPostFrontmatter = {
      title,
      platform,
      status,
      ...(sourceEntityId && { sourceEntityId }),
      ...(sourceEntityType && { sourceEntityType }),
    };

    const postContent = socialPostAdapter.createPostContent(
      frontmatter,
      content,
    );
    const partial = socialPostAdapter.fromMarkdown(postContent);
    const metadata = partial.metadata;

    if (!metadata) {
      this.failEarly("Failed to parse social post metadata");
    }

    // Ensure title doesn't collide
    const finalTitle = await ensureUniqueTitle({
      entityType: "social-post",
      title,
      deriveId: (t) => `${platform}-${slugify(t)}`,
      regeneratePrompt:
        "Generate a different social media post title on the same topic.",
      context: this.context,
    });

    let finalContent = postContent;
    if (finalTitle !== title) {
      metadata.title = finalTitle;
      metadata.slug = `${platform}-${slugify(finalTitle)}`;
      // Rebuild content with updated title in frontmatter
      const updatedFrontmatter: SocialPostFrontmatter = {
        ...frontmatter,
        title: finalTitle,
      };
      finalContent = socialPostAdapter.createPostContent(
        updatedFrontmatter,
        content,
      );
    }

    return {
      id: metadata.slug,
      content: finalContent,
      metadata,
      title: finalTitle,
      resultExtras: { slug: metadata.slug },
      createOptions: { deduplicateId: true },
    };
  }

  protected override async onGenerationFailure(
    _data: GenerationJobData,
    error: string,
  ): Promise<void> {
    await this.context.messaging.send({
      type: GENERATE_CHANNELS.reportFailure,
      payload: {
        entityType: "social-post",
        error,
      },
    });
  }

  protected override async afterCreate(
    data: GenerationJobData,
    entityId: string,
    progressReporter: ProgressReporter,
    generated: GeneratedContent,
  ): Promise<void> {
    // Queue image generation if requested
    if (data.generateImage) {
      await this.reportProgress(progressReporter, {
        progress: 90,
        message: "Queueing image generation",
      });

      const title = generated.title ?? "Social Post";
      await this.context.jobs.enqueue({
        type: IMAGE_CHANNELS.generate,
        data: {
          prompt: `Social media graphic for: ${title}`,
          title: `${title} Image`,
          aspectRatio: "16:9",
          targetEntityType: "social-post",
          targetEntityId: entityId,
        },
        toolContext: {
          interfaceType: "job",
          actor: { kind: "service", serviceId: "social-media-generation" },
        },
      });
    }

    await this.context.messaging.send({
      type: GENERATE_CHANNELS.reportSuccess,
      payload: {
        entityType: "social-post",
        entityId,
      },
    });
  }

  protected override summarizeDataForLog(
    data: GenerationJobData,
  ): Record<string, unknown> {
    return {
      platform: data.platform ?? "linkedin",
      hasPrompt: !!data.prompt,
      sourceEntityType: data.sourceEntityType,
      addToQueue: data.addToQueue ?? false,
      generateImage: data.generateImage ?? false,
      coverImage: !!data.coverImage,
    };
  }
}
